import { useState } from 'react';
import { Plus } from 'lucide-react';
import { SectionHeading } from '../../components/SectionHeading';

const FAQS: ReadonlyArray<{ q: string; a: string }> = [
  {
    q: 'How does a project start?',
    a: "With a scoping conversation, not a contract. We map the workflow, the data, and the outcome you need, then come back with a concrete plan and a fixed scope. You see exactly what you're committing to before you commit to it.",
  },
  {
    q: 'How long does a build take?',
    a: "It depends entirely on scope — an agent on your WhatsApp number is a different job from an ERP covering your whole operation. You get a timeline in the scoping plan, before any commitment, and we work in short cycles so you see working software throughout rather than at the end.",
  },
  {
    q: 'Do you work with the systems we already have?',
    a: 'Yes — that is usually the point. Our agents call existing APIs, and our platforms integrate with the tools your team already runs. We would rather connect what works than force a rebuild of it.',
  },
  {
    q: 'Where does everything get deployed?',
    a: 'Your cloud or ours. We build and run on AWS, DigitalOcean, Railway, Hetzner, and PythonAnywhere, with CI/CD and monitoring set up as part of delivery. If you have an existing cloud account and policies, we deploy inside them.',
  },
  {
    q: 'What happens after launch?',
    a: 'We stay on. Support, iteration, and the next module when you need it — the systems we build are running in production for clients today, and that is the part we take most seriously.',
  },
  {
    q: 'Is Pulse built on the official WhatsApp API?',
    a: 'Yes. Pulse runs on the official WhatsApp Business API — no gray-market gateways and no risk to your number. Your customers keep messaging the number they already know.',
  },
  {
    q: 'Can Lumen be customized to how we work?',
    a: 'That is the reason it exists. Lumen is modular, so you run only the modules you need and add more later. Fields, workflows, and reports are shaped to your process, and when configuration is not enough we extend the platform for you.',
  },
];

export function Faq(): React.JSX.Element {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section id="faq">
      <div className="container">
        <SectionHeading
          anim="fade"
          title={
            <>
              Questions, <span className="si accent-text">answered straight</span>.
            </>
          }
        />
        <div className="faq-list">
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={item.q} className={`faq-item reveal ${isOpen ? 'open' : ''}`}>
                <h3>
                  <button
                    type="button"
                    className="faq-q"
                    aria-expanded={isOpen}
                    aria-controls={`faq-panel-${i}`}
                    id={`faq-btn-${i}`}
                    onClick={() => setOpen(isOpen ? null : i)}
                  >
                    <span>{item.q}</span>
                    <Plus className="faq-icon" size={20} aria-hidden="true" />
                  </button>
                </h3>
                <div
                  className="faq-panel"
                  id={`faq-panel-${i}`}
                  role="region"
                  aria-labelledby={`faq-btn-${i}`}
                  hidden={!isOpen}
                >
                  <p>{item.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
